import { Component, OnInit } from "@angular/core";
import { BaseComponent } from "../shared/base.component";
import { Subject } from "rxjs";
import { WebStorageService } from "../services/web-storage.service";
import { Router } from "@angular/router";
import { ActivityLogService } from "../services/activity-log.service";
import { finalize, takeUntil } from "rxjs/operators";
import { PagedResult, ResultOfT } from "../models/response/result";
import { ActivityLogResponseModel, AdminActivityLogResponseModel } from "../models/response/activity-log-response";
import { GetAdminActivityLogsRequestModel } from "../models/request/activity-log";
import { environment } from "src/environments/environment";
import { LazyLoadEvent } from "primeng/api";
import { UserAuthResponseModel } from "../models/response/access-token-response";
import * as moment from "moment";
import { formatDate } from "@angular/common";
import { saveAs as importedSaveAs } from 'file-saver';

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.sass']
})
export class ActivityLogComponent extends BaseComponent implements OnInit {
  value: number = 0;
  messageClass: boolean = false;
  message: string = '';
  displayMessage: boolean = false;
  filterResposniveEvent: boolean = false;
  interfaceList: any[] = [];
  selectedCompany: any = null;
  showClearFilter: boolean = false;
  displayClearButton: boolean = false;
  searchText: string = '';
  activityLogs: any[];
  activityLogsBackup: any[] = [];
  products: any[] = [];
  isCustomerAdmin: boolean = false;
  private unsubscriber = new Subject<boolean>();
  private progress = new Subject<boolean>();
  currentUserToken: UserAuthResponseModel;
  companyId: number;
  logType: any;
  performedBy: string;
  loading: boolean;
  pageNumber: number = 1;
  pageSize: number = 50; // Default page size
  totalRecords: number;
  isLazyLoadingEnabled: boolean = false;
  sortField: string; // Default sort field
  sortOrder: number = -1;
  logTypeOptions: { label: any; value: any; }[];
  fromDate: Date | null = null;
  toDate: Date | null = null;
  selectedDateRange: Date[] | null = null;


  constructor(private webStorageService: WebStorageService,
    private router: Router,
    private activityLogService: ActivityLogService) {
    super();

    this.currentUserToken = this.webStorageService.getCurrentUserToken();
    this.companyId = this.currentUserToken.companyId;

  }

  ngOnInit(): void {
    this.getLogTypes();
    setTimeout(() => {
      this.triggerInitialLoad();
    }, 100);
  }

  triggerInitialLoad(): void {
    const event = {
      first: 0,
      rows: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    } as LazyLoadEvent;

    this.onLazyLoad(event);
  }

  getLogTypes() {
    this.loading = true;
    this.activityLogService.GetAdminLogTypes()
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<string[]>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            if (result.ResponseModel && result.ResponseModel.length > 0) {
              this.logTypeOptions = result.ResponseModel.map(type => ({
                label: type,
                value: type
              }));
              this.loading = false;
            }
          }
          else {
            this.loading = false;
            this.timeoutFunct(result.Message, false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  async onLazyLoad(event: LazyLoadEvent) {
    if (!this.activityLogs) {
      this.activityLogs = [];
    }
    if (!event.rows || event.rows == this.pageSize) {
      this.activityLogs = [];
      this.pageNumber = 1;
      this.totalRecords = 0;
      this.isLazyLoadingEnabled = false;
      this.loading = true;
    } else {
      this.isLazyLoadingEnabled = true;
      const totalLoadedRecords = this.activityLogs?.length || 0;
      if (totalLoadedRecords >= this.totalRecords && this.activityLogs.length > 0) {
        return;
      }
      this.pageNumber = Math.floor(totalLoadedRecords / this.pageSize) + 1;
      this.loading = true;
    }

    let sortOrderString = 'desc';
    if (event.sortOrder === 1) {
      sortOrderString = 'asc';
    } else if (event.sortOrder === -1) {
      sortOrderString = 'desc';
    }
    const requestModel = new GetAdminActivityLogsRequestModel();
    requestModel.CompanyId = this.companyId;
    requestModel.PageNumber = this.pageNumber;
    requestModel.PageSize = this.pageSize;
    requestModel.SearchText = this.searchText || '';
    requestModel.SortColumn = event.sortField ? event.sortField.toString() : '';
    requestModel.SortOrder = sortOrderString;
    requestModel.LogType = this.logType || '';
    requestModel.PerformedBy = this.performedBy || '';
    this.sortField = event.sortField ? event.sortField.toString() : '';
    this.sortOrder = sortOrderString === 'asc' ? 1 : -1;
    if (this.selectedDateRange && this.selectedDateRange.length === 2) {
      // requestModel.FromDate = this.selectedDateRange[0];
      requestModel.FromDate = formatDate(moment(this.selectedDateRange[0]).toDate(), 'yyyy-MM-dd', 'en-US');
      requestModel.ToDate = formatDate(moment(this.selectedDateRange[1]).toDate(), 'yyyy-MM-dd', 'en-US');
    } else {
      requestModel.FromDate = undefined;
      requestModel.ToDate = undefined;
    }

    this.activityLogService.GetAdminActivityLogs(requestModel)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<PagedResult<AdminActivityLogResponseModel>>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            if (result.ResponseModel && result.ResponseModel.Data) {
              this.totalRecords = result?.ResponseModel?.TotalRecords || 0;
              const newRecords = result.ResponseModel.Data;
              newRecords.forEach(item => {
                if (item.CreatedDate) {
                  item.CreatedDate = moment.utc(item.CreatedDate).local().toDate();
                }
              });
              if (this.pageNumber === 1) {
                this.activityLogs = [...newRecords];
              } else {
                const finalRecordList = [...this.activityLogs, ...newRecords];
                this.activityLogs = finalRecordList;
              }
              this.isLazyLoadingEnabled = true;
            }
          } else {
            this.timeoutFunct(result.Message, false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  // Component methods
  isActive(): boolean {
    return true;
  }

  closeMessagePopup(): void {
    this.displayMessage = false;
    this.message = '';
  }

  filterClickEvent(): void {
    this.filterResposniveEvent = !this.filterResposniveEvent;
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
    const event = {
      first: 0,
      rows: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    } as LazyLoadEvent;

    this.onLazyLoad(event);
  }


  timeoutFunct(msg: string, isSuccess: boolean): void {
    this.displayMessage = true;
    this.messageClass = isSuccess;
    this.message = msg;
    setTimeout(() => {
      this.message = '';
      this.displayMessage = false;
    }, environment.messageLifeSpan);
  }

  applyFilters(): void {
    this.showClearFilter = true;
    this.displayClearButton = true;
    this.filterResposniveEvent = false;
    const event = {
      first: 0,
      rows: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    } as LazyLoadEvent;

    this.onLazyLoad(event);
  }

  resetFilter(): void {
    this.selectedCompany = null;
    this.searchText = '';
    this.logType = '';
    this.performedBy = '';
    this.fromDate = undefined;
    this.toDate = undefined;
    this.selectedDateRange = null;
    this.showClearFilter = false;
    this.displayClearButton = false;
    const event = {
      first: 0,
      rows: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    } as LazyLoadEvent;

    this.filterResposniveEvent = false;
    this.onLazyLoad(event);
  }

  exportToCsv(): void {
    this.loading = true;

    const requestModel = new GetAdminActivityLogsRequestModel();
    requestModel.PageNumber = 1;
    // requestModel.PageSize = this.activityLogs.length || this.pageSize;
    requestModel.PageSize = this.totalRecords;
    requestModel.SearchText = this.searchText || '';
    requestModel.SortColumn = this.sortField || 'CreatedDate';
    requestModel.SortOrder = this.sortOrder === 1 ? 'asc' : 'desc';
    requestModel.LogType = this.logType || '';
    requestModel.PerformedBy = this.performedBy || '';
    requestModel.CompanyId = this.companyId;
    if (this.selectedDateRange && this.selectedDateRange.length === 2) {
      requestModel.FromDate = formatDate(moment(this.selectedDateRange[0]).toDate(), 'yyyy-MM-dd', 'en-US');
      requestModel.ToDate = formatDate(moment(this.selectedDateRange[1]).toDate(), 'yyyy-MM-dd', 'en-US');
    } else {
      requestModel.FromDate = undefined;
      requestModel.ToDate = undefined;
    }
    this.activityLogService.ExportAdminActivityLogsCsv(requestModel)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((blob: Blob) => {
        importedSaveAs(blob, 'AdminActivityLogs.csv');
      }, (error) => {
        this.loading = false;
        this.timeoutFunct('Error exporting CSV: ' + error.message, false);
      });
  }

  ngOnDestroy(): void {
    if (this.unsubscriber) {
      this.unsubscriber.next(true);
      this.unsubscriber.complete();
    }
  }
}
